import { eachDayOfInterval, format } from "date-fns";
import { TrendingDown, TrendingUp } from "lucide-react";
import type * as React from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PanelHeader } from "@/components/panel-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import type { ManagerReportResponse } from "@/types/reports";

type ManagerDashboardChartsProps = {
  report?: ManagerReportResponse | null;
  isLoading?: boolean;
  startDate: Date;
  endDate: Date;
  firstSiteId?: string;
};

type ActivityChartRow = {
  day: string;
  total: number;
};

type SiteChartRow = {
  siteName: string;
  value: number;
};

const activityColor = "var(--chart-2)";
const siteSeriesColor = "var(--chart-1)";
const activityHoverColor = "var(--chart-1)";
const siteHoverColor = "var(--chart-2)";
const barHoverCursor = {
  fill: "var(--accent)",
  fillOpacity: 0.45,
};

function SiteChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color?: string; name?: string; value?: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <ChartTooltipContent
      active={active}
      payload={payload}
      label={label}
      indicator="line"
    />
  );
}

function getTrendLabel(currentValue: number, previousValue: number) {
  if (previousValue <= 0) {
    return {
      direction: "up" as const,
      label:
        currentValue > 0
          ? `Started with ${currentValue} records`
          : "No movement this period",
    };
  }

  const percentChange = ((currentValue - previousValue) / previousValue) * 100;
  const roundedPercent = Math.abs(percentChange).toFixed(1);

  if (percentChange >= 0) {
    return {
      direction: "up" as const,
      label: `Trending up by ${roundedPercent}% in this window`,
    };
  }

  return {
    direction: "down" as const,
    label: `Trending down by ${roundedPercent}% in this window`,
  };
}

function ChartState({
  title,
  description,
  children,
  footerTitle,
  footerDescription,
  footerTrend,
  isLoading,
  emptyTitle,
  emptyDescription,
  emptyAction,
  icon,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  footerTitle: string;
  footerDescription: string;
  footerTrend?: "up" | "down";
  isLoading?: boolean;
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  const TrendIcon = footerTrend
    ? footerTrend === "down"
      ? TrendingDown
      : TrendingUp
    : null;

  return (
    <Card className="h-full overflow-hidden py-0">
      <div className="px-6 pt-6">
        <PanelHeader title={title} description={description} icon={icon} />
      </div>
      <CardContent className="px-4 pt-3">
        {isLoading ? (
          <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed bg-muted/20 text-sm text-muted-foreground">
            Loading chart…
          </div>
        ) : (
          (children ?? (
            <Empty className="min-h-[180px] rounded-lg">
              <EmptyHeader>
                <EmptyTitle>{emptyTitle}</EmptyTitle>
                <EmptyDescription>{emptyDescription}</EmptyDescription>
              </EmptyHeader>
              {emptyAction ? <EmptyContent>{emptyAction}</EmptyContent> : null}
            </Empty>
          ))
        )}
      </CardContent>
      <CardFooter className="flex-row items-center justify-between gap-4 border-t border-border/60 px-6 pt-4 pb-6">
        <div className="flex min-w-0 items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
          <span>{footerTitle}</span>
          {TrendIcon ? <TrendIcon className="h-4 w-4" /> : null}
        </div>
        <div className="shrink-0 text-xs text-muted-foreground">
          {footerDescription}
        </div>
      </CardFooter>
    </Card>
  );
}

export function ManagerDashboardCharts({
  report,
  isLoading,
  startDate,
  endDate,
  firstSiteId,
}: ManagerDashboardChartsProps) {
  const activityChartData: ActivityChartRow[] = eachDayOfInterval({
    start: startDate,
    end: endDate,
  }).map((day) => {
    const dayKey = format(day, "yyyy-MM-dd");
    const dailySignIns =
      report?.recentSignIns.filter(
        (entry) => format(new Date(entry.signInTime), "yyyy-MM-dd") === dayKey
      ) ?? [];

    return {
      day: format(day, "EEE"),
      total: dailySignIns.length,
    };
  });

  const siteChartData: SiteChartRow[] =
    report?.siteBreakdown.slice(0, 5).map((site) => ({
      siteName: site.siteName,
      value: site.signInCount,
    })) ?? [];

  const activityTrend = getTrendLabel(
    activityChartData.at(-1)?.total ?? 0,
    activityChartData[0]?.total ?? 0
  );
  const topSite = report?.siteBreakdown[0];
  const periodLabel = `${format(startDate, "MMMM")} - ${format(endDate, "MMMM yyyy")}`;
  const hasActivity = activityChartData.some((entry) => entry.total > 0);
  const activityCtaHref = firstSiteId
    ? `/sites/${firstSiteId}/qr`
    : "/sites/new";
  const activityCtaLabel = firstSiteId ? "Open site QR" : "Add first site";

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <ChartState
        title="Weekly Sign-Ins"
        description="Showing total sign-ins for the last 7 days"
        footerTitle={
          hasActivity ? activityTrend.label : "No sign-ins in this window"
        }
        footerDescription={periodLabel}
        footerTrend={hasActivity ? activityTrend.direction : undefined}
        isLoading={isLoading && activityChartData.length === 0}
        emptyTitle="No activity yet"
        emptyDescription="Daily sign-ins will appear here once crews start checking in from the site QR code."
        emptyAction={
          <Button asChild size="sm" variant="outline">
            <Link to={activityCtaHref}>{activityCtaLabel}</Link>
          </Button>
        }
      >
        {hasActivity ? (
          <ChartContainer
            config={{
              total: { label: "Sign-ins", color: activityColor },
            }}
          >
            <ResponsiveContainer width="100%" height={210}>
              <BarChart
                data={activityChartData}
                margin={{ left: 12, right: 12, top: 12, bottom: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--color-border)"
                  strokeOpacity={0.55}
                />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  tick={{ fill: "var(--color-muted-foreground)", fontSize: 14 }}
                />
                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "var(--color-muted-foreground)", fontSize: 13 }}
                  tickMargin={8}
                  width={28}
                />
                <Tooltip
                  content={<SiteChartTooltip />}
                  cursor={barHoverCursor}
                />
                <Bar
                  dataKey="total"
                  name="Sign-ins"
                  fill="var(--color-total)"
                  fillOpacity={0.9}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={42}
                  activeBar={{
                    fill: activityHoverColor,
                    fillOpacity: 1,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : null}
      </ChartState>

      <ChartState
        title="Site Coverage"
        description="Showing busiest sites across the selected period"
        footerTitle={
          topSite
            ? `${topSite.siteName} led with ${topSite.signInCount} sign-ins`
            : "No site activity in this window"
        }
        footerDescription={periodLabel}
        footerTrend="up"
        isLoading={isLoading && siteChartData.length === 0}
        emptyTitle="No site activity yet"
        emptyDescription="Site sign-ins will appear here once workers start checking in."
      >
        {siteChartData.length > 0 ? (
          <ChartContainer
            config={{
              value: { label: "Sign-ins", color: siteSeriesColor },
            }}
          >
            <ResponsiveContainer
              width="100%"
              height={Math.max(180, siteChartData.length * 44)}
            >
              <BarChart
                data={siteChartData}
                layout="vertical"
                margin={{ left: 0, right: 20, top: 8, bottom: 0 }}
              >
                <CartesianGrid
                  horizontal={false}
                  stroke="var(--color-border)"
                  strokeOpacity={0.55}
                />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "var(--color-muted-foreground)", fontSize: 13 }}
                  tickMargin={8}
                />
                <YAxis
                  type="category"
                  dataKey="siteName"
                  tickLine={false}
                  axisLine={false}
                  width={110}
                  tick={{ fill: "var(--color-muted-foreground)", fontSize: 13 }}
                  tickFormatter={(value: string) =>
                    value.length > 14 ? `${value.slice(0, 14)}…` : value
                  }
                />
                <Tooltip
                  content={<SiteChartTooltip />}
                  cursor={barHoverCursor}
                />
                <Bar
                  dataKey="value"
                  name="Sign-ins"
                  radius={[0, 4, 4, 0]}
                  maxBarSize={28}
                  activeBar={{
                    fill: siteHoverColor,
                    fillOpacity: 1,
                  }}
                >
                  {siteChartData.map((entry) => (
                    <Cell
                      key={entry.siteName}
                      fill={siteSeriesColor}
                      fillOpacity={0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : null}
      </ChartState>
    </div>
  );
}
